import { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useLoginWithSms, usePrivy } from '@privy-io/react-auth'
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input'
import type { E164Number } from 'libphonenumber-js'
import 'react-phone-number-input/style.css'
import { Button, Header, Icon } from '../components/ui'
import { useToast } from '../hooks/useToast'

export function LoginScreen() {
  const navigate = useNavigate()
  const location = useLocation()
  const [phone, setPhone] = useState<E164Number | undefined>(
    import.meta.env.DEV ? '+15555557415' as E164Number : undefined
  )
  const [error, setError] = useState<string | null>(null)
  const toast = useToast()
  const phoneRef = useRef<E164Number | undefined>(phone)

  // Track if user came back from OTP screen (to avoid auto-redirect loop)
  const cameFromOtp = useRef(location.state?.fromOtp === true)
  const phoneContainerRef = useRef<HTMLDivElement>(null)

  // Scroll input into view when keyboard appears (viewport resizes)
  useEffect(() => {
    const viewport = window.visualViewport
    if (!viewport) return

    const handleResize = () => {
      const input = phoneContainerRef.current?.querySelector('input')
      if (input && document.activeElement === input) {
        input.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }

    viewport.addEventListener('resize', handleResize)
    return () => viewport.removeEventListener('resize', handleResize)
  }, [])

  const { authenticated, ready } = usePrivy()

  const { sendCode, state } = useLoginWithSms({
    onError: (error) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const msg = (error as any).message || 'Failed to send code. Please try again.'
      setError(msg)
      toast.error(msg)
    },
  })

  // Keep ref in sync with phone state
  useEffect(() => {
    phoneRef.current = phone
  }, [phone])

  // Navigate to OTP screen when code is sent successfully
  // Skip if user just came back from OTP
  useEffect(() => {
    if (state.status === 'awaiting-code-input' && !cameFromOtp.current) {
      navigate('/otp', { state: { phoneNumber: phoneRef.current } })
    }
  }, [state.status, navigate])

  // Reset the flag when user submits the form (new login attempt)
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    cameFromOtp.current = false // Allow navigation on new submit

    // Allow Privy test numbers (555 prefix) in dev, or when explicitly enabled (e.g. staging)
    const allowTestNumbers = import.meta.env.DEV || import.meta.env.VITE_ALLOW_TEST_NUMBERS === 'true'
    const isTestNumber = phone?.includes('555') && allowTestNumbers
    if (!phone || (!isTestNumber && !isValidPhoneNumber(phone))) {
      const msg = 'Please enter a valid phone number'
      setError(msg)
      toast.error(msg)
      return
    }

    sendCode({ phoneNumber: phone })
  }

  // Show loading while Privy initializes
  if (!ready) {
    return (
      <div className="min-h-[100dvh] w-full bg-[var(--color-background)] flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[var(--color-secondary)] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        </div>
      </div>
    )
  }

  // Redirect if already authenticated
  if (authenticated) {
    navigate('/capture')
    return null
  }

  const isSending = state.status === 'sending-code'

  return (
    <div className="min-h-[100dvh] w-full bg-[var(--color-background)] flex flex-col">
      <Header showLogo />

      {/* Main Content - Centered */}
      <main className="flex-1 flex flex-col justify-center items-center px-6 w-full max-w-md mx-auto">
        {/* Icon & Title */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-50 mb-4">
            <Icon name="lock" size={32} className="text-[var(--color-secondary)]" />
          </div>
          <h2 className="text-2xl font-semibold text-[var(--color-text)] mb-2">
            Sign in to continue
          </h2>
          <p className="text-[var(--color-text-muted)] text-sm">
            Enter your phone number to get started
          </p>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="w-full space-y-6">
          <div className="w-full" ref={phoneContainerRef}>
            <label className="block text-lg font-medium text-[var(--color-text)] mb-2">
              Phone Number
            </label>
            <PhoneInput
              international
              countryCallingCodeEditable={false}
              defaultCountry="US"
              countries={['US', 'TR']}
              value={phone}
              onChange={(value) => {
                setPhone(value)
                setError(null)
              }}
              className={`phone-input ${error ? 'phone-input--error' : ''}`}
            />
            {error && (
              <p className="mt-2 text-sm text-[var(--color-error)]">{error}</p>
            )}
          </div>

          <Button type="submit" disabled={isSending || !phone}>
            {isSending ? 'Sending...' : 'Continue'}
            {!isSending && <Icon name="arrow-right" size={20} />}
          </Button>
        </form>

        {/* Terms */}
        <div className="mt-8 text-center px-4">
          <p className="text-sm text-[var(--color-text-muted)] leading-relaxed">
            By continuing, you agree to our{' '}
            <a href="https://finchnow.com/finch-pay-privacy" target="_blank" rel="noopener noreferrer" className="font-semibold text-[var(--color-secondary)] hover:underline">
              Privacy Policy
            </a>
            {' '}and{' '}
            <a href="https://finchnow.com/finch-pay-terms" target="_blank" rel="noopener noreferrer" className="font-semibold text-[var(--color-secondary)] hover:underline">
              Terms of Use
            </a>
          </p>
        </div>
        
      </main>

      {/* Safe area spacer for bottom */}
      <div className="h-8 flex-shrink-0" />
    </div>
  )
}
